import { useId, useState } from "react";
import type { FormEvent } from "react";
import { problemFieldErrors, problemMessage } from "@/api/client";
import type {
  Animal,
  AnimalGroup,
  CreateAnimalRequest,
  Guid,
  Herd,
  UpdateAnimalRequest,
} from "@/api/types";
import { DialogShell } from "./DialogShell";
import "./AnimalDialog.css";

/**
 * ef-041-5 — the "New animal" / "Edit animal" dialog Livestock.tsx opens from its own
 * header action or a row's Edit button. A dialog component, deliberately separate from
 * Livestock.tsx's own CSS budget — same reasoning `ShiftDialog.tsx`/`W4Dialog.tsx` give
 * for splitting out of their own page: `deploy/lib/css-budget.sh` gates `pages/*.css` at
 * 250 lines, and component CSS living beside a component under `components/` is the
 * intended escape valve (ADR 0009 §5).
 *
 * TAG IS SET ONCE, ON CREATE, AND NEVER SHOWN AS EDITABLE AFTER — `UpdateAnimalRequest`
 * (api/types.ts, mirroring `LivestockContracts.cs`) has no `tag` field at all, so an
 * editable input in edit mode would be a control that always does nothing when used. Edit
 * mode instead states the tag as plain text, same pattern `ShiftDialog.tsx` uses for its
 * own once-only `employeeId`.
 *
 * STATUS NEVER APPEARS HERE. A status change (with its required audit trail) is its own
 * row action on Livestock.tsx, never a field on this form — `UpdateAnimalRequest` carries
 * no `status` member on purpose (see that type's own remarks).
 */
interface AnimalFormState {
  tag: string;
  herdId: string;
  groupId: string;
  sex: string;
  breed: string;
  birthDate: string;
  weightCurrent: string;
  // ef-059-6 — the record fields.
  brand: string;
  color: string;
  ownerName: string;
  notes: string;
  twin: boolean;
}

function blankForm(): AnimalFormState {
  return {
    tag: "",
    herdId: "",
    groupId: "",
    sex: "",
    breed: "",
    birthDate: "",
    weightCurrent: "",
    brand: "",
    color: "",
    ownerName: "",
    notes: "",
    twin: false,
  };
}

function formFromAnimal(animal: Animal): AnimalFormState {
  return {
    tag: animal.tag,
    herdId: animal.herdId ?? "",
    groupId: animal.groupId ?? "",
    sex: animal.sex ?? "",
    breed: animal.breed ?? "",
    birthDate: animal.birthDate ?? "",
    weightCurrent: animal.weightCurrent != null ? String(animal.weightCurrent) : "",
    brand: animal.brand ?? "",
    color: animal.color ?? "",
    ownerName: animal.ownerName ?? "",
    notes: animal.notes ?? "",
    twin: animal.twin,
  };
}

/** Client-side mirror of the server's record-field limits (counted in code points — see handleSubmit). */
const RECORD_LIMITS = (f: AnimalFormState): [string, string, number, string][] => [
  ["brand", "Brand", 80, f.brand],
  ["color", "Color", 40, f.color],
  ["ownerName", "Owner", 120, f.ownerName],
  ["notes", "Notes", 2000, f.notes],
];

export function AnimalDialog({
  entityId,
  herds,
  groups,
  animal,
  onClose,
  onCreate,
  onUpdate,
  onSaved,
}: {
  /** The tenant's own entity id (`useLivestock.ts`'s own `entityId`) — required to create
   *  an animal at all. Livestock.tsx never opens this dialog for CREATE before it
   *  resolves; it stays available in edit mode purely for type convenience. */
  entityId: Guid | null;
  herds: Herd[];
  groups: AnimalGroup[];
  /** `null` opens the dialog in create mode; an `Animal` opens it pre-seeded for editing. */
  animal: Animal | null;
  /** Called on Cancel, Escape, an overlay click, AND after a successful save — same
   *  division `ShiftDialog.tsx` uses between "close the dialog" and "where focus goes
   *  next", which stays the caller's job (it holds the trigger ref). */
  onClose: () => void;
  onCreate: (body: CreateAnimalRequest) => Promise<Animal>;
  onUpdate: (id: Guid, body: UpdateAnimalRequest) => Promise<Animal>;
  /** ef-059-6 — handed the saved `Animal` (its `warnings` included) just before `onClose`. */
  onSaved?: (animal: Animal) => void;
}) {
  const [form, setForm] = useState<AnimalFormState>(() => (animal ? formFromAnimal(animal) : blankForm()));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const tagId = useId();
  const herdIdFieldId = useId();
  const groupIdFieldId = useId();
  const sexId = useId();
  const breedId = useId();
  const birthDateId = useId();
  const weightId = useId();
  const brandId = useId();
  const colorId = useId();
  const ownerId = useId();
  const notesId = useId();
  const twinId = useId();

  const formId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);

    /*
     * @ui-check: client-side ONLY — the server's own checks (LivestockEndpoints.cs's
     * ValidateTag/ValidateWeight/etc.) still run after this and remain the actual
     * contract enforcement. This guard exists so a farmer sees the same reason
     * immediately, without a round trip.
     */
    const missing: Record<string, string[]> = {};
    if (!animal && form.tag.trim() === "") missing.tag = ["Tag is required."];
    const weight = form.weightCurrent.trim() === "" ? null : Number(form.weightCurrent);
    if (weight !== null && (!Number.isFinite(weight) || weight <= 0)) {
      missing.weightCurrent = ["Must be greater than zero."];
    }
    // Length limits count Unicode code points, not UTF-16 units — the server does the same, so an
    // emoji is 1 character here as there. (A `maxLength` attribute counts UTF-16 units: it would
    // refuse 1,001 astral characters the server accepts.) The server stays the authority.
    for (const [key, label, limit, value] of RECORD_LIMITS(form)) {
      if ([...value.trim()].length > limit) missing[key] = [`${label} must be ${limit} characters or fewer.`];
    }
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setFormError(Object.values(missing).flat()[0] ?? null);
      return;
    }

    setSaving(true);
    try {
      const sex = form.sex.trim() === "" ? undefined : form.sex.trim();
      const breed = form.breed.trim() === "" ? undefined : form.breed.trim();
      const birthDate = form.birthDate.trim() === "" ? undefined : form.birthDate.trim();
      const herdId = form.herdId === "" ? undefined : form.herdId;
      const groupId = form.groupId === "" ? undefined : form.groupId;
      const weightCurrent = weight ?? undefined;

      // On edit a blank string CLEARS the field server-side (omitted would leave it unchanged);
      // on create a blank is just omitted.
      const record = (v: string): string | undefined => (animal ? v.trim() : v.trim() === "" ? undefined : v.trim());
      const brand = record(form.brand);
      const color = record(form.color);
      const ownerName = record(form.ownerName);
      const notes = record(form.notes);

      let saved: Animal;
      if (animal) {
        saved = await onUpdate(animal.id, {
          herdId,
          groupId,
          sex,
          breed,
          birthDate,
          weightCurrent,
          brand,
          color,
          ownerName,
          notes,
          twin: form.twin,
        });
      } else {
        if (entityId === null) {
          setFormError("No entity found for this tenant yet.");
          setSaving(false);
          return;
        }
        saved = await onCreate({
          entityId,
          tag: form.tag.trim(),
          herdId,
          groupId,
          sex,
          breed,
          birthDate,
          weightCurrent,
          brand,
          color,
          ownerName,
          notes,
          twin: form.twin,
        });
      }
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not save this animal."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title={animal ? `Edit animal — ${animal.tag}` : "New animal"}
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {/* Exact labels, not "Save changes" — livestock.spec.ts's own save-button
                locator is `/^(save|create|add)( animal)?$/i` for a create and
                `/^(save|update)( animal)?$/i` for an edit, both anchored at both ends. */}
            {saving ? "Saving…" : animal ? "Save" : "Save animal"}
          </button>
        </>
      }
    >
        <form id={formId} className="animal-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          {animal ? (
            <p className="animal-dialog__tag">{animal.tag}</p>
          ) : (
            <div className="animal-dialog__field">
              <label htmlFor={tagId}>Tag</label>
              <input
                id={tagId}
                type="text"
                maxLength={50}
                value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value })}
                aria-invalid={fieldErrors?.tag ? true : undefined}
              />
              {fieldErrors?.tag && (
                <p className="animal-dialog__field-error" role="alert">
                  {fieldErrors.tag[0]}
                </p>
              )}
            </div>
          )}

          <div className="animal-dialog__field">
            <label htmlFor={herdIdFieldId}>Herd</label>
            <select
              id={herdIdFieldId}
              value={form.herdId}
              onChange={(e) => setForm({ ...form, herdId: e.target.value })}
            >
              <option value="">Unassigned</option>
              {herds.map((herd) => (
                <option key={herd.id} value={herd.id}>
                  {herd.name}
                </option>
              ))}
            </select>
          </div>

          <div className="animal-dialog__field">
            <label htmlFor={groupIdFieldId}>Group</label>
            <select
              id={groupIdFieldId}
              value={form.groupId}
              onChange={(e) => setForm({ ...form, groupId: e.target.value })}
            >
              <option value="">Unassigned</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          <div className="animal-dialog__field">
            <label htmlFor={sexId}>Sex (optional)</label>
            <input
              id={sexId}
              type="text"
              maxLength={20}
              value={form.sex}
              onChange={(e) => setForm({ ...form, sex: e.target.value })}
            />
          </div>

          <div className="animal-dialog__field">
            <label htmlFor={breedId}>Breed (optional)</label>
            <input
              id={breedId}
              type="text"
              maxLength={80}
              value={form.breed}
              onChange={(e) => setForm({ ...form, breed: e.target.value })}
            />
          </div>

          <div className="animal-dialog__field">
            <label htmlFor={birthDateId}>Birth date (optional)</label>
            <input
              id={birthDateId}
              type="date"
              value={form.birthDate}
              onChange={(e) => setForm({ ...form, birthDate: e.target.value })}
            />
          </div>

          <div className="animal-dialog__field">
            <label htmlFor={weightId}>Current weight (lbs, optional)</label>
            <input
              id={weightId}
              type="number"
              min="0.01"
              step="0.1"
              value={form.weightCurrent}
              onChange={(e) => setForm({ ...form, weightCurrent: e.target.value })}
              aria-invalid={fieldErrors?.weightCurrent ? true : undefined}
            />
            {fieldErrors?.weightCurrent && (
              <p className="animal-dialog__field-error" role="alert">
                {fieldErrors.weightCurrent[0]}
              </p>
            )}
          </div>

          <div className="animal-dialog__field">
            <label htmlFor={brandId}>Brand (optional)</label>
            <input
              id={brandId}
              type="text"
              aria-invalid={fieldErrors?.brand ? true : undefined}
              value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
            />
          </div>

          <div className="animal-dialog__field">
            <label htmlFor={colorId}>Color (optional)</label>
            <input
              id={colorId}
              type="text"
              aria-invalid={fieldErrors?.color ? true : undefined}
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
            />
          </div>

          <div className="animal-dialog__field">
            <label htmlFor={ownerId}>Owner (optional)</label>
            <input
              id={ownerId}
              type="text"
              aria-invalid={fieldErrors?.ownerName ? true : undefined}
              value={form.ownerName}
              onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
            />
          </div>

          <div className="animal-dialog__field">
            <label htmlFor={notesId}>Notes (optional)</label>
            <textarea
              id={notesId}
              rows={3}
              aria-invalid={fieldErrors?.notes ? true : undefined}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <div className="animal-dialog__check">
            <input
              id={twinId}
              type="checkbox"
              checked={form.twin}
              onChange={(e) => setForm({ ...form, twin: e.target.checked })}
            />
            <label htmlFor={twinId}>Twin</label>
          </div>

          {formError && !fieldErrors?.tag && !fieldErrors?.weightCurrent && (
            <p className="animal-dialog__form-error" role="alert">
              {formError}
            </p>
          )}
        </form>
    </DialogShell>
  );
}
